'use client';

/**
 * MFA setup — ui-ux-pro-max gold/cream shell
 * QR + backup codes + verification steps with consistent a11y.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Form, Input, Button, message } from 'antd';
import {
  SafetyOutlined,
  QrcodeOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/domains/auth/context/auth-context';
import { useTheme, ThemeToggle } from '@/shared/theme';
import '@/shared/theme/styles/login.css';
import styles from './AuthLayout.module.css';

interface MFASetupData {
  secret?: string;
  qr_code?: string;
  backup_codes?: string[];
  totp_uri?: string;
}

const STEP_LABELS = ['Scan QR', 'Backup codes', 'Verify', 'Complete'] as const;

const MFASetupPage: React.FC = () => {
  const [form] = Form.useForm();
  const router = useRouter();
  const { setupMFA, verifyMFASetup } = useAuth();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [mfaSetupData, setMfaSetupData] = useState<MFASetupData | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const initializeMFASetup = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await setupMFA();
      if (result.success && result.data) {
        setMfaSetupData(result.data);
        setCurrentStep(0);
      } else {
        setError(result.error || 'Failed to initialize MFA setup');
      }
    } catch (err) {
      console.error('MFA setup initialization error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize MFA setup');
    } finally {
      setLoading(false);
    }
  }, [setupMFA]);

  useEffect(() => {
    initializeMFASetup();
  }, [initializeMFASetup]);

  const handleVerifyToken = async () => {
    if (!verificationToken.trim()) {
      message.error('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await verifyMFASetup(verificationToken);
      if (result.success) {
        setCurrentStep(3);
        message.success('MFA setup completed successfully!');
        setTimeout(() => {
          router.push('/dashboard');
        }, 2000);
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success(`${label} copied to clipboard`);
      })
      .catch(() => {
        message.error('Failed to copy to clipboard');
      });
  };

  const downloadBackupCodes = () => {
    if (!mfaSetupData?.backup_codes) return;

    const content = `Mechagora Trading Platform - MFA Backup Codes

IMPORTANT: Keep these codes in a safe place. You can use them to access your account if you lose your authenticator app.

Backup Codes:
${(mfaSetupData.backup_codes || []).map((code, index) => `${index + 1}. ${code}`).join('\n')}

Generated: ${new Date().toISOString()}
Platform: Mechagora Trading Platform

Security Note: Each code can only be used once.`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mechagora-mfa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    message.success('Backup codes downloaded');
  };

  const shell = (children: React.ReactNode, wide = true) => (
    <div
      className={`login-page ${theme.isDark ? 'dark' : 'light'} ${styles.page}`}
      data-theme={theme.isDark ? 'dark' : 'light'}
    >
      <nav className="login-navbar" aria-label="Primary">
        <div className="login-navbar-container">
          <a href="/" className="login-navbar-brand">
            <div className="login-logo-icon" aria-hidden>
              <span className="login-logo-text">M</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="login-brand-title">Mechagora</span>
              <span className="login-brand-subtitle">Security</span>
            </div>
          </a>
          <ThemeToggle size={isMobile ? 'small' : 'medium'} />
        </div>
      </nav>
      <div className={`${styles.body} ${styles.bodyTop}`}>
        <div className={`${styles.formContainer} ${wide ? styles.formContainerWide : ''}`}>
          {children}
        </div>
      </div>
    </div>
  );

  if (loading && !mfaSetupData) {
    return shell(
      <div className={styles.card}>
        <div className={styles.loadingState}>
          <SafetyOutlined className={styles.passkeyIcon} aria-hidden />
          <p className={styles.loadingTitle}>Setting up MFA…</p>
          <p className={styles.loadingText}>Please wait while we prepare your security setup.</p>
        </div>
      </div>,
      false
    );
  }

  if (error && !mfaSetupData) {
    return shell(
      <div className={styles.card}>
        <div className={styles.errorAlert} role="alert">
          <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
          <div className={styles.errorContent}>
            <div className={styles.errorTitle}>MFA setup error</div>
            <div className={styles.errorText}>{error}</div>
          </div>
        </div>
        <div className={styles.actionRow}>
          <Button type="primary" className={styles.submitBtn} onClick={initializeMFASetup} style={{ flex: 1, marginTop: 0 }}>
            Try again
          </Button>
          <Button className={styles.secondaryBtn} onClick={() => router.push('/dashboard')} style={{ flex: 1 }}>
            Skip for now
          </Button>
        </div>
      </div>,
      false
    );
  }

  return shell(
    <>
      <header className={styles.formHeaderCenter} style={{ marginBottom: 20 }}>
        <div className={styles.heroIcon} aria-hidden>
          <SafetyOutlined />
        </div>
        <h1 className={styles.formTitle}>Set up two-factor authentication</h1>
        <p className={styles.formSubtitle}>
          Enhance your account security with multi-factor authentication
        </p>
      </header>

      <div className={styles.stepsCard} aria-label="Setup progress">
        <div className={styles.stepDots} role="list">
          {STEP_LABELS.map((label, i) => (
            <div
              key={label}
              role="listitem"
              aria-current={i === currentStep ? 'step' : undefined}
              title={label}
              className={`${styles.stepDot} ${i <= currentStep ? styles.stepDotActive : ''}`}
            />
          ))}
        </div>
        <p className={styles.formSubtitle} style={{ textAlign: 'center', margin: 0 }}>
          Step {Math.min(currentStep + 1, STEP_LABELS.length)} of {STEP_LABELS.length}:{' '}
          {STEP_LABELS[Math.min(currentStep, STEP_LABELS.length - 1)]}
        </p>
      </div>

      {error && (
        <div className={styles.errorAlert} role="alert" id="mfa-setup-error">
          <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
          <div className={styles.errorContent}>
            <div className={styles.errorTitle}>Setup error</div>
            <div className={styles.errorText}>{error}</div>
          </div>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => setError(null)}
            className={styles.dismissBtn}
            aria-label="Dismiss error"
          />
        </div>
      )}

      {currentStep === 0 && mfaSetupData && (
        <div className={styles.card}>
          <div className={styles.sectionTitle}>
            <QrcodeOutlined aria-hidden style={{ marginRight: 8 }} />
            Scan QR code
          </div>

          <div className={styles.qrBox}>
            <Image
              src={mfaSetupData.qr_code || ''}
              alt="MFA QR code for authenticator app"
              width={260}
              height={260}
              style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
            />
          </div>

          <ol
            style={{
              textAlign: 'left',
              maxWidth: 440,
              margin: '0 auto 20px',
              color: 'var(--login-text-muted)',
              fontSize: '0.9rem',
              lineHeight: 1.55,
              paddingLeft: 20,
            }}
          >
            <li>Install an authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)</li>
            <li>Open the app and tap Add Account or +</li>
            <li>Scan the QR code above</li>
            <li>The app will add Mechagora Trading Platform</li>
          </ol>

          <p className={styles.fieldLabel} style={{ textAlign: 'center' }}>
            Manual setup (if QR doesn&apos;t work)
          </p>
          <div className={styles.manualSecret}>
            <Input value={mfaSetupData.secret || ''} readOnly aria-label="Manual setup secret key" />
            <Button
              icon={<CopyOutlined aria-hidden />}
              className={styles.generateBtn}
              onClick={() => copyToClipboard(mfaSetupData.secret || '', 'Secret key')}
            >
              Copy
            </Button>
          </div>

          <Button
            type="primary"
           
            block
            className={styles.submitBtn}
            icon={<ArrowRightOutlined />}
            iconPlacement="end"
            onClick={() => setCurrentStep(1)}
            disabled={loading}
            style={{ marginTop: 24 }}
          >
            Next: Backup codes
          </Button>
        </div>
      )}

      {currentStep === 1 && mfaSetupData && (
        <div className={styles.card}>
          <div className={styles.sectionTitle}>
            <KeyOutlined aria-hidden style={{ marginRight: 8 }} />
            Save backup codes
          </div>

          <div className={styles.warningBox} role="note">
            These backup codes can unlock your account if you lose your authenticator app. Each code
            works once — store them somewhere safe.
          </div>

          <div className={styles.actionRow}>
            <Button
              icon={showBackupCodes ? <EyeInvisibleOutlined aria-hidden /> : <EyeOutlined aria-hidden />}
              onClick={() => setShowBackupCodes(!showBackupCodes)}
              className={styles.secondaryBtn}
              style={{ height: 44 }}
            >
              {showBackupCodes ? 'Hide' : 'Show'} codes
            </Button>
            <Button
              icon={<CopyOutlined aria-hidden />}
              onClick={() =>
                copyToClipboard((mfaSetupData.backup_codes || []).join('\n'), 'Backup codes')
              }
              className={styles.generateBtn}
            >
              Copy all
            </Button>
            <Button
              icon={<DownloadOutlined aria-hidden />}
              onClick={downloadBackupCodes}
              className={styles.generateBtn}
            >
              Download
            </Button>
          </div>

          {showBackupCodes && (
            <div className={styles.backupGrid} aria-label="Backup codes">
              {(mfaSetupData.backup_codes || []).map((code, index) => (
                <div key={index} className={styles.backupCode}>
                  {code}
                </div>
              ))}
            </div>
          )}

          <div className={styles.actionRow} style={{ marginTop: 8 }}>
            <Button className={styles.secondaryBtn} onClick={() => setCurrentStep(0)} style={{ flex: 1 }}>
              Back
            </Button>
            <Button
              type="primary"
              className={styles.submitBtn}
              onClick={() => setCurrentStep(2)}
              disabled={loading}
              icon={<ArrowRightOutlined />}
              iconPlacement="end"
              style={{ flex: 1, marginTop: 0 }}
            >
              Next: Verify
            </Button>
          </div>
        </div>
      )}

      {currentStep === 2 && mfaSetupData && (
        <div className={styles.card}>
          <div className={styles.sectionTitle}>
            <SafetyOutlined aria-hidden style={{ marginRight: 8 }} />
            Verify setup
          </div>
          <p className={styles.formSubtitle} style={{ marginBottom: 20, textAlign: 'center' }}>
            Open your authenticator app and enter the 6-digit code for Mechagora Trading Platform
          </p>

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            className={styles.formFields}
            onFinish={handleVerifyToken}
          >
            <Form.Item
              name="token"
              htmlFor="mfa-setup-token"
              label={<span className={styles.fieldLabel}>Verification code</span>}
              rules={[
                { required: true, message: 'Enter the verification code' },
                { len: 6, message: 'Code must be 6 digits' },
                { pattern: /^\d{6}$/, message: 'Code must contain only numbers' },
              ]}
            >
              <Input
                id="mfa-setup-token"
                placeholder="000000"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                className={styles.mfaInput}
                value={verificationToken}
                onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, ''))}
                aria-required="true"
                aria-describedby={error ? 'mfa-setup-error' : undefined}
              />
            </Form.Item>

            <div className={styles.actionRow}>
              <Button className={styles.secondaryBtn} onClick={() => setCurrentStep(1)} style={{ flex: 1 }}>
                Back
              </Button>
              <Button
                type="primary"
                htmlType="submit"
               
                loading={loading}
                className={styles.submitBtn}
                style={{ flex: 1, marginTop: 0 }}
              >
                Verify & complete
              </Button>
            </div>
          </Form>

          <p className={styles.formFootnote}>
            Having trouble? You can also use one of your backup codes.
          </p>
        </div>
      )}

      {currentStep === 3 && (
        <div className={styles.card} style={{ textAlign: 'center' }}>
          <CheckCircleOutlined className={styles.completeIcon} aria-hidden />
          <h2 className={styles.formTitle} style={{ color: '#16a34a' }}>
            Two-factor authentication enabled
          </h2>
          <p className={styles.formSubtitle} style={{ marginBottom: 24 }}>
            Your account is now protected with multi-factor authentication. You&apos;ll be
            redirected to the dashboard shortly.
          </p>
          <Button
            type="primary"
           
            block
            className={styles.submitBtn}
            onClick={() => router.push('/dashboard')}
          >
            Go to dashboard
          </Button>
        </div>
      )}
    </>
  );
};

export default MFASetupPage;
